import { faTrashCan, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { boundsForList, fitBoundsIfRelevant } from "../map/util";
import { useCaMap, useConfig, useProjects } from "../state";
import { ProjectChat } from "./ProjectChat";
import { ProjectFiles } from "./ProjectFiles";

type Tab = "parcels" | "files";

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={
      "px-2 py-1 text-xs font-semibold border-b-2 transition-colors " +
      (active
        ? "border-bright-yellow text-black"
        : "border-transparent text-gray-400 hover:text-black")
    }
  >
    {children}
  </button>
);

const ParcelThumbnail = ({ center }: { center: { coordinates: [number, number] } }) => {
  const config = useConfig();
  const gps = `${center.coordinates[0]},${center.coordinates[1]}`;
  const img = `${config.referenceUrl}/api/image/cadastre?c=${gps}&width=64&height=64`;
  const img2x = `${config.referenceUrl}/api/image/cadastre?c=${gps}&width=128&height=128`;
  return (
    <img
      src={img}
      srcSet={`${img} 1x, ${img2x} 2x`}
      alt=""
      className="w-10 h-10 rounded object-cover shrink-0 bg-gray-100"
    />
  );
};

const ParcelRow = ({ parcel, projectId }: { parcel: any; projectId: number }) => {
  const navigate = useNavigate();
  const removeParcelFromProject = useProjects((s) => s.removeParcelFromProject);

  const address = parcel.data?.addresses?.[0] as
    | { street?: string; number?: string; city?: string }
    | undefined;
  const addressLine = address
    ? [[address.street, address.number].filter(Boolean).join(" "), address.city]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <li className="flex items-start gap-2 rounded hover:bg-gray-50 px-2 py-1.5 group">
      <button
        onClick={() =>
          navigate(
            `/location/${parcel.center.coordinates[0].toFixed(5)},${parcel.center.coordinates[1].toFixed(5)}`
          )
        }
        className="flex items-start gap-2 text-left flex-1 min-w-0"
      >
        <ParcelThumbnail center={parcel.center} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-black truncate">{parcel.name}</div>
          {addressLine && <div className="text-xs text-gray-600 truncate">{addressLine}</div>}
          {parcel.description && (
            <div className="text-xs text-gray-500 truncate">{parcel.description}</div>
          )}
        </div>
      </button>
      <button
        onClick={() => {
          if (confirm("Perceel verwijderen?")) removeParcelFromProject(projectId, parcel.id);
        }}
        className="text-gray-300 hover:text-red-500 group-hover:opacity-100 ml-1 mt-1"
      >
        <FontAwesomeIcon icon={faTrashCan} size="xs" />
      </button>
    </li>
  );
};

export const ProjectBar = () => {
  const { projects, selectedProjectId, selectProject } = useProjects();
  const map = useCaMap();
  const deselectProject = useCallback(() => selectProject(null), [selectProject]);
  const [tab, setTab] = useState<Tab>("parcels");

  const project = projects?.find((p) => p.id === selectedProjectId);
  if (!project) return null;

  const parcels = project.parcels || [];

  return (
    <div className="fixed right-2 top-16 z-10 w-80 h-[60vh] max-h-[calc(100vh-5rem)] resize-y overflow-hidden bg-white rounded-lg shadow-lg border border-gray-200 font-Gilroy text-sm flex flex-col">
      <div className="flex items-center justify-between p-3 pb-0">
        <button
          onClick={() => fitBoundsIfRelevant(map, boundsForList(project.parcels))}
          className="font-bold text-black hover:text-blue-500 truncate text-left"
        >
          {project.name}
        </button>
        <button onClick={deselectProject} className="text-gray-400 hover:text-black p-1">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div>
        <div className="flex border-bgap-1">
          <TabButton active={tab === "parcels"} onClick={() => setTab("parcels")}>
            Percelen <span className="text-gray-400 font-normal">({parcels.length})</span>
          </TabButton>
          <TabButton active={tab === "files"} onClick={() => setTab("files")}>
            Bestanden
          </TabButton>
        </div>
      </div>
      <div className="overflow-y-auto min-h-0 max-h-32 sm:max-h-[35%] shrink-0">
        {tab === "parcels" ? (
          parcels.length === 0 ? (
            <p className="text-gray-400 text-xs p-2">
              Klik op een perceel op de kaart en voeg het toe
            </p>
          ) : (
            <ul className="space-y-1 p-2">
              {parcels.map((parcel) => (
                <ParcelRow key={parcel.id} parcel={parcel} projectId={project.id} />
              ))}
            </ul>
          )
        ) : (
          <ProjectFiles projectId={project.id} />
        )}
      </div>
      {parcels.length > 0 && <ProjectChat projectId={project.id} />}
    </div>
  );
};
